import axiosHttp from "services/axiosHttp";

const { createSlice, createAsyncThunk } = require("@reduxjs/toolkit");

export const fetchGroupUsers = createAsyncThunk(
	"fetchGroupUsers",
	async ({ id, inGroup, page = 1, limit }, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.get(`/groups/${id}/users`, {
				params: { page, limit, inGroup },
			});

			return {
				inGroup,
				data: response.data.data,
			};
		} catch (error) {
			return rejectWithValue({
				inGroup,
				message: error?.response?.data?.message || "Failed to load group users",
			});
		}
	},
);

export const fetchGroupUsersByIds = createAsyncThunk(
	"fetchGroupUsersByIds",
	async ({ ids = [], page = 1, limit }, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.get("/groups/users", {
				params: { page, limit, ids: ids.join(",") },
			});

			return response.data.data;
		} catch (error) {
			return rejectWithValue(error?.response?.data?.message || "Failed to load selected group users");
		}
	},
);

export const fetchAllGroupUsers = createAsyncThunk(
	"fetchAllGroupUsers",
	async ({ page = 1, limit }, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.get("/groups/users", {
				params: { page, limit },
			});

			return response.data.data;
		} catch (error) {
			return rejectWithValue(error?.response?.data?.message || "Failed to load group users");
		}
	},
);

const GroupUsersSlice = createSlice({
	name: "GroupUsersSlice",
	initialState: {
		inGroupLoading: false,
		notInGroupLoading: false,
		allUsersLoading: false,
		selectedUsersLoading: false,
		inGroupUsers: null,
		notInGroupUsers: null,
		allUsers: null,
		selectedUsers: null,
		inGroupError: null,
		notInGroupError: null,
		allUsersError: null,
		selectedUsersError: null,
	},
	reducers: {
		resetGroupUsers: (state) => {
			state.inGroupLoading = false;
			state.notInGroupLoading = false;
			state.allUsersLoading = false;
			state.selectedUsersLoading = false;
			state.inGroupUsers = null;
			state.notInGroupUsers = null;
			state.allUsers = null;
			state.selectedUsers = null;
			state.inGroupError = null;
			state.notInGroupError = null;
			state.allUsersError = null;
			state.selectedUsersError = null;
		},
		resetSelectedGroupUsers: (state) => {
			state.selectedUsersLoading = false;
			state.selectedUsers = null;
			state.selectedUsersError = null;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchGroupUsers.pending, (state, action) => {
			if (action.meta.arg.inGroup) {
				state.inGroupLoading = true;
				state.inGroupError = null;
			} else {
				state.notInGroupLoading = true;
				state.notInGroupError = null;
			}
		});

		builder.addCase(fetchGroupUsers.fulfilled, (state, action) => {
			if (action.payload?.inGroup) {
				state.inGroupLoading = false;
				state.inGroupUsers = action.payload.data;
			} else {
				state.notInGroupLoading = false;
				state.notInGroupUsers = action.payload.data;
			}
		});

		builder.addCase(fetchGroupUsers.rejected, (state, action) => {
			if (action.payload?.inGroup) {
				state.inGroupLoading = false;
				state.inGroupError = action.payload.message;
			} else {
				state.notInGroupLoading = false;
				state.notInGroupError = action.payload?.message;
			}
		});

		builder.addCase(fetchGroupUsersByIds.pending, (state) => {
			state.selectedUsersLoading = true;
			state.selectedUsersError = null;
		});

		builder.addCase(fetchGroupUsersByIds.fulfilled, (state, action) => {
			state.selectedUsersLoading = false;
			state.selectedUsers = action.payload;
		});

		builder.addCase(fetchGroupUsersByIds.rejected, (state, action) => {
			state.selectedUsersLoading = false;
			state.selectedUsersError = action.payload || action.error?.message;
		});

		builder.addCase(fetchAllGroupUsers.pending, (state) => {
			state.allUsersLoading = true;
			state.allUsersError = null;
		});

		builder.addCase(fetchAllGroupUsers.fulfilled, (state, action) => {
			state.allUsersLoading = false;
			state.allUsers = action.payload;
		});

		builder.addCase(fetchAllGroupUsers.rejected, (state, action) => {
			state.allUsersLoading = false;
			state.allUsersError = action.payload || action.error?.message;
		});
	},
});

export const { resetGroupUsers, resetSelectedGroupUsers } = GroupUsersSlice.actions;

export default GroupUsersSlice.reducer;
